import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Buildings,
  Plus,
  PencilSimple,
  Trash,
  CurrencyEur,
  FileText,
  FilePdf,
  Circle,
  MagnifyingGlass,
  Paperclip,
  Upload,
  Spinner,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import MarchesHub from './MarchesHub';
import DpgfDceConsistencyBanner from './DpgfDceConsistencyBanner';
// R5 (D26 A) — consultation dans Marchés : frise par lot, DCE (déplacé de Conception), avenants
import MarchesFrise from './MarchesFrise';
import DCEManager from './DCEManager';
import AvenantsMarche from './AvenantsMarche';
// A5 / P4#8 — « Nouvel avenant » depuis la carte du lot (l'éditeur reste celui de la DPGF)
import { AvenantEditor } from './DPGFAvenantsManager';
// Phase E frontend megasprint
import { BOUTONS } from '../../lib/libelles';
import { useAuth } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import { projectRights } from './projectRights';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_MAP = {
  non_attribue: { label: 'Non attribué', color: '#9A9A9A', bg: '#F5F5F3' },
  // CSL-05 — statut posé à l'invitation DCE : doit rester sélectionnable à l'édition
  en_consultation: { label: 'En consultation', color: '#0369A1', bg: '#E0F2FE' },
  attribue: { label: 'Attribué', color: '#1E3A5F', bg: '#EFF6FF' },
  en_cours: { label: 'En cours', color: '#D4880F', bg: '#FFFBEB' },
  receptionne: { label: 'Réceptionné', color: '#2D6A4F', bg: '#F0FDF4' },
  contentieux: { label: 'Contentieux', color: '#C53030', bg: '#FEF2F2' },
};

const EMPTY_LOT = {
  code: '', nom: '', corps_etat: '',
  entreprise_id: '',
  entreprise_nom: '', entreprise_siret: '', entreprise_email: '', entreprise_tel: '', entreprise_contact: '',
  montant_marche: '', montant_avenant: 0,
  date_debut: '', date_fin_prevue: '',
  statut: 'non_attribue',
  ordre_service_date: '', ordre_service_ref: '',
};

export default function ProjectMarches({ chantierId, onImporter }) {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_LOT });
  const [saving, setSaving] = useState(false);
  // Phase E frontend megasprint
  const [docsLot, setDocsLot] = useState(null); // Sprint D #23 — Documents modal
  // RP02 — boutons d'écriture affichés seulement si le serveur les accepte :
  // lots, devis et documents de marché = rôles projet MOE (archi, moe_principal,
  // moe_membre) ; import DPGF = archi (ou droit « Modifier le DPGF » accordé).
  // L'AMO (consultant) et l'entreprise gardent la lecture.
  const { user } = useAuth();
  const { perms, role_projet } = usePermissions(chantierId);
  const { canWriteMarches, canImportDpgf } = projectRights({ userRole: user?.role, roleProjet: role_projet, perms });
  // RP61 — la consultation (DCE) s'ouvre dans Marchés, depuis la frise
  const [showDce, setShowDce] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // A6 (P2#6, #7) — la frise est LA liste des lots ; la vue détaillée et l'édition manuelle
  // passent dans « Avancé » (ouvert d'office s'il n'y a encore aucun lot).
  const [nbLotsFrise, setNbLotsFrise] = useState(null);
  const [avanceOuvert, setAvanceOuvert] = useState(false);
  const dceRef = useRef(null);
  const ouvrirConsultation = () => {
    setShowDce(true);
    setTimeout(() => dceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };
  const toutRecharger = () => { fetchLots(); setRefreshKey((k) => k + 1); };
  const canDemanderAvis = ['archi', 'moe_principal'].includes(role_projet);
  // A5 / P4#8 — avenant créé depuis la carte du lot (architecte, chef de projet)
  const canCreerAvenant = ['archi', 'moe_principal'].includes(role_projet);
  const [avenantOuvert, setAvenantOuvert] = useState(null);
  const nouvelAvenant = async (lot) => {
    try {
      const { data: av } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/dpgf/avenants`,
        { label: `Avenant — lot ${lot.code} ${lot.nom || ''}`.trim() }, { withCredentials: true });
      // Le lot de l'avenant porte le numéro du lot : rattachement automatique au marché (P4#7)
      await axios.put(`${API_URL}/api/chantiers/${chantierId}/dpgf/avenants/${av.id}`,
        { label: av.label, motif: '', lots: [{ nom: `${lot.code} - ${lot.nom || ''}`.trim(), ref_lot_id: lot.dpgf_lot_id || null, postes: [] }] },
        { withCredentials: true });
      setAvenantOuvert(av.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de créer l'avenant");
    }
  };
  const voitAvenants = ['archi', 'moe_principal', 'moe_membre', 'amo'].includes(role_projet);

  useEffect(() => { fetchLots(); }, [chantierId]);

  const fetchLots = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/marches`, { withCredentials: true });
      setLots(data);
    } catch (err) {
      console.error('Error fetching lots:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_LOT });
    setDialogOpen(true);
  };

  const openEdit = (lot) => {
    setEditing(lot);
    setForm({
      code: lot.code || '',
      nom: lot.nom || '',
      corps_etat: lot.corps_etat || '',
      entreprise_id: lot.entreprise_id || '',
      entreprise_nom: lot.entreprise_nom || '',
      entreprise_siret: lot.entreprise_siret || '',
      entreprise_email: lot.entreprise_email || '',
      entreprise_tel: lot.entreprise_tel || '',
      entreprise_contact: lot.entreprise_contact || '',
      montant_marche: lot.montant_marche ?? '',
      montant_avenant: lot.montant_avenant ?? 0,
      date_debut: lot.date_debut || '',
      date_fin_prevue: lot.date_fin_prevue || '',
      statut: lot.statut || 'non_attribue',
      ordre_service_date: lot.ordre_service_date || '',
      ordre_service_ref: lot.ordre_service_ref || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.nom) return toast.error('Code et nom du lot requis');
    setSaving(true);
    try {
      const payload = {
        ...form,
        montant_marche: form.montant_marche ? parseFloat(form.montant_marche) : null,
        montant_avenant: form.montant_avenant ? parseFloat(form.montant_avenant) : 0,
      };
      if (editing) {
        await axios.put(`${API_URL}/api/chantiers/${chantierId}/marches/${editing.id}`, payload, { withCredentials: true });
        toast.success('Lot mis à jour');
      } else {
        await axios.post(`${API_URL}/api/chantiers/${chantierId}/marches`, payload, { withCredentials: true });
        toast.success('Lot ajouté');
      }
      setDialogOpen(false);
      fetchLots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lot) => {
    if (!window.confirm(`Supprimer le lot ${lot.code} — ${lot.nom} ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/chantiers/${chantierId}/marches/${lot.id}`, { withCredentials: true });
      toast.success('Lot supprimé');
      fetchLots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  // A5 / P4#3 — tout lot qui a un marché compte, quel que soit son statut (En cours,
  // Réceptionné…) : même total que Finances et Point MOA.
  const aUnMarche = (l) => l.montant_marche != null && Number(l.montant_marche) > 0;
  const totalMarche = lots.reduce((s, l) => s + (aUnMarche(l) ? Number(l.montant_marche) : 0), 0);
  const totalAvenant = lots.reduce((s, l) => s + (aUnMarche(l) ? (l.montant_avenant || 0) + (l.montant_avenants_acceptes || 0) : 0), 0);
  // Même montant que « Marchés HT » de Finances : initial + avenants acceptés par le client
  const totalAvenantsAcceptes = lots.reduce((s, l) => s + (aUnMarche(l) ? (l.montant_avenants_acceptes || 0) : 0), 0);

  const fmt = (n) => n != null ? n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) : '—';

  return (
    <div data-testid="project-marches">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Entreprises & Marchés
          </h2>
          <p className="text-sm text-[#6B6B6B] mt-1" data-testid="marches-total">
            {Math.max(lots.length, nbLotsFrise || 0)} lot(s) — Total des marchés : {fmt(totalMarche)}
            {totalAvenantsAcceptes !== 0 && ` · avec avenants acceptés : ${fmt(totalMarche + totalAvenantsAcceptes)}`}
          </p>
        </div>
        {(canWriteMarches || canImportDpgf) && (
        <div className="flex items-center gap-2 flex-wrap">
          {/* Fluidité B2 (P2#17) — « Importer DPGF » et « Uploader un devis » : une seule entrée
              « Importer » (ImporterDialog), qui ouvre les mêmes fenêtres (DPGFImportDialog,
              DevisUploadDialog) avec les mêmes droits (import DPGF, écriture des marchés). */}
          {onImporter && (canImportDpgf || canWriteMarches) && (
          <Button
            variant="outline"
            className="rounded-none gap-2"
            onClick={onImporter}
            data-testid="import-dpgf-button"
          >
            <Upload size={16} /> {BOUTONS.importer}
          </Button>
          )}
          {canWriteMarches && (
          <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none gap-2" onClick={openCreate} data-testid="add-lot-button">
            <Plus size={18} weight="bold" /> Ajouter un lot
          </Button>
          )}
        </div>
        )}
      </div>

      {/* R5 / RP60-62 — frise par lot : préparé → consulté → attribué → signé → en cours → réceptionné */}
      <MarchesFrise
        chantierId={chantierId}
        roleProjet={role_projet}
        canWrite={canWriteMarches}
        canDemanderAvis={canDemanderAvis}
        onOuvrirConsultation={ouvrirConsultation}
        refreshKey={refreshKey}
        onChanged={fetchLots}
        actionsLot={canCreerAvenant ? (lot) => (lot.marche_initial_ht != null && (
          <Button size="sm" variant="outline" className="rounded-none text-xs gap-1 border-[#1E3A5F] text-[#1E3A5F]"
            onClick={() => nouvelAvenant(lot)} data-testid={`frise-nouvel-avenant-${lot.id}`}>
            <Plus size={12} /> Nouvel avenant
          </Button>
        )) : undefined}
        onLots={setNbLotsFrise}
      />
      {avenantOuvert && (
        <AvenantEditor open chantierId={chantierId} avenantId={avenantOuvert}
          onClose={() => { setAvenantOuvert(null); toutRecharger(); }} onSaved={() => {}} />
      )}

      {/* RP61 — consultation des entreprises (DCE), déplacée de Conception */}
      {canWriteMarches && (
        <div ref={dceRef} className="mb-6" data-testid="marches-consultation">
          {showDce ? (
            <div className="border border-[#E0E0DB] bg-white p-4">
              <DCEManager chantierId={chantierId} chantierName="" onBack={() => { setShowDce(false); toutRecharger(); }} />
            </div>
          ) : (nbLotsFrise === 0 || nbLotsFrise === null) && (
            // A6 (P2#7) — un seul bouton de consultation : celui de la frise ; ici seulement sans lot
            <Button variant="outline" className="rounded-none gap-2" onClick={ouvrirConsultation} data-testid="marches-ouvrir-consultation">
              <FileText size={16} /> Préparer la consultation
            </Button>
          )}
        </div>
      )}

      {/* A6 (P2#7) — vue détaillée (hub) et édition manuelle : repliées sous « Avancé » */}
      {nbLotsFrise > 0 && (
        <button type="button" onClick={() => setAvanceOuvert((o) => !o)} data-testid="marches-avance"
          className="text-xs text-[#1E3A5F] underline hover:no-underline mb-4">
          {avanceOuvert ? 'Masquer la vue détaillée et l\'édition manuelle des lots' : 'Avancé : vue détaillée et édition manuelle des lots'}
        </button>
      )}
      {/* l'alerte de cohérence DPGF ↔ consultation reste visible, vue détaillée repliée */}
      {nbLotsFrise > 0 && !avanceOuvert && canWriteMarches && (
        <DpgfDceConsistencyBanner chantierId={chantierId} onActionComplete={toutRecharger} />
      )}
      {(avanceOuvert || nbLotsFrise === 0) && (<>
      {/* Hub Marchés — vue agrégée propositions / attribution */}
      <MarchesHub chantierId={chantierId} onChanged={toutRecharger} canWrite={canWriteMarches} />

      </>)}
      {/* RP67-68 — avenants soumis à l'accord du client (avis AMO facultatif) */}
      {voitAvenants && (
        <AvenantsMarche chantierId={chantierId} roleProjet={role_projet} onChanged={toutRecharger} />
      )}
      {(avanceOuvert || nbLotsFrise === 0) && (<>

      <div className="border-t border-[#E0E0DB] my-6 pt-6">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B] mb-3" style={{ fontFamily: 'DM Serif Display, serif' }}>
          {canWriteMarches ? 'Liste des lots — Édition manuelle' : 'Liste des lots'}
        </h3>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14" />)}
        </div>
      ) : lots.length === 0 ? (
        <div className="card-kroquis text-center py-16">
          <Buildings size={64} className="mx-auto text-[#E0E0DB] mb-4" />
          <h3 className="text-lg font-semibold text-[#2C2C2C] mb-2">Aucun lot défini</h3>
          {canWriteMarches ? (
            <>
              <p className="text-[#6B6B6B] mb-6">Ajoutez les lots du marché avec leurs entreprises attributaires</p>
              <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none gap-2" onClick={openCreate}>
                <Plus size={16} /> Ajouter le premier lot
              </Button>
            </>
          ) : (
            <p className="text-[#6B6B6B]">L'architecte n'a pas encore défini les lots de ce projet.</p>
          )}
        </div>
      ) : (
        <div className="border border-[#E0E0DB] bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="marches-table">
            <thead>
              <tr className="border-b border-[#E0E0DB] bg-[#FAFAF8]">
                <th className="text-left py-3 px-4 font-semibold text-[#6B6B6B] text-xs uppercase tracking-wider">Lot</th>
                <th className="text-left py-3 px-4 font-semibold text-[#6B6B6B] text-xs uppercase tracking-wider">Corps d'état</th>
                <th className="text-left py-3 px-4 font-semibold text-[#6B6B6B] text-xs uppercase tracking-wider">Entreprise</th>
                <th className="text-right py-3 px-4 font-semibold text-[#6B6B6B] text-xs uppercase tracking-wider">Montant marché</th>
                <th className="text-center py-3 px-4 font-semibold text-[#6B6B6B] text-xs uppercase tracking-wider">Statut</th>
                <th className="text-center py-3 px-4 font-semibold text-[#6B6B6B] text-xs uppercase tracking-wider w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lots.map((lot) => {
                const st = STATUT_MAP[lot.statut] || STATUT_MAP.non_attribue;
                return (
                  <tr key={lot.id} className="border-b border-[#E0E0DB] hover:bg-[#FAFAF8] transition-colors" data-testid={`lot-row-${lot.id}`}>
                    <td className="py-3 px-4">
                      <span className="font-semibold text-[#2C2C2C]">{lot.code}</span>
                      <br />
                      <span className="text-[#6B6B6B] text-xs">{lot.nom}</span>
                    </td>
                    <td className="py-3 px-4 text-[#6B6B6B]">{lot.corps_etat || '—'}</td>
                    <td className="py-3 px-4">
                      {lot.entreprise_nom ? (
                        <div>
                          <span className="font-medium text-[#2C2C2C]">{lot.entreprise_nom}</span>
                          {lot.entreprise_contact && <span className="text-xs text-[#6B6B6B] block">{lot.entreprise_contact}</span>}
                        </div>
                      ) : (
                        <span className="text-[#9A9A9A] italic">Non attribué</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-[#2C2C2C]" data-testid={`lot-montant-${lot.id}`}>
                      {aUnMarche(lot) ? fmt(lot.montant_marche) : '—'}
                      {aUnMarche(lot) && lot.montant_avenant > 0 && (
                        <span className="text-xs text-[#D4880F] block">+{fmt(lot.montant_avenant)} avenant</span>
                      )}
                      {aUnMarche(lot) && !!lot.montant_avenants_acceptes && (
                        <span className="text-xs text-[#D4880F] block" data-testid={`lot-avenants-acceptes-${lot.id}`}>
                          {lot.montant_avenants_acceptes > 0 ? '+' : ''}{fmt(lot.montant_avenants_acceptes)} avenants acceptés
                          <span className="block font-semibold text-[#1E3A5F]">= {fmt((lot.montant_marche || 0) + lot.montant_avenants_acceptes)}</span>
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1"
                        style={{ background: st.bg, color: st.color }}
                      >
                        <Circle size={6} weight="fill" />
                        {st.label}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <a
                          href={`${API_URL}/api/chantiers/${chantierId}/rapport-lots/${lot.id}/export-pdf`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center h-8 w-8 p-0 hover:bg-[#F5F5F3] text-[#6B4A24] hover:text-[#C75B39]"
                          title="Rapport PDF du lot (transmissible à l'entreprise)"
                          data-testid={`rapport-lot-${lot.id}`}
                        >
                          <FilePdf size={14} />
                        </a>
                        {canWriteMarches && (
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(lot)} data-testid={`edit-lot-${lot.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                        )}
                        {/* Sprint D #23 — Documents marché (post-attribution) */}
                        {lot.statut !== 'non_attribue' && (
                          <Button variant="ghost" size="sm" className={`relative h-8 w-8 p-0 ${(lot.documents || []).length ? 'text-[#1E3A5F]' : 'text-[#6B6B6B]'}`} onClick={() => setDocsLot(lot)} title={`Documents (${(lot.documents || []).length})`} data-testid={`docs-lot-${lot.id}`}>
                            <Paperclip size={14} />
                            {(lot.documents || []).length > 0 && (
                              <span className="absolute -top-1 -right-1 bg-[#C75B39] text-white text-[8px] px-1 rounded-full">{(lot.documents || []).length}</span>
                            )}
                          </Button>
                        )}
                        {canWriteMarches && (
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#C53030] hover:text-[#C53030]" onClick={() => handleDelete(lot)} data-testid={`delete-lot-${lot.id}`}>
                          <Trash size={14} />
                        </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-[#FAFAF8] border-t-2 border-[#E0E0DB]">
                <td colSpan={3} className="py-3 px-4 font-semibold text-[#2C2C2C] text-right">Total marchés</td>
                <td className="py-3 px-4 text-right font-bold text-[#2C2C2C]">{fmt(totalMarche)}</td>
                <td colSpan={2} className="py-3 px-4 text-center text-xs text-[#6B6B6B]">
                  {totalAvenant > 0 && `+${fmt(totalAvenant)} avenants`}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      </>)}
      {/* Sprint D #23 — Modale documents marché */}
      {docsLot && (
        <MarcheDocumentsDialog
          chantierId={chantierId}
          canWrite={canWriteMarches}
          lot={docsLot}
          onClose={() => setDocsLot(null)}
          onChanged={fetchLots}
        />
      )}

      {/* Dialog Create/Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>
              {editing ? `Modifier ${editing.code} — ${editing.nom}` : 'Ajouter un lot'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Lot info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Code lot *</Label>
                <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="LOT01" required className="rounded-none mt-1" disabled={!!editing} data-testid="lot-code-input" />
              </div>
              <div className="md:col-span-2">
                <Label>Nom du lot *</Label>
                <Input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} placeholder="Gros Oeuvre" required className="rounded-none mt-1" data-testid="lot-nom-input" />
              </div>
            </div>
            <div>
              <Label>Corps d'état</Label>
              <Input value={form.corps_etat} onChange={e => setForm({ ...form, corps_etat: e.target.value })} placeholder="Maçonnerie, CVC, Électricité..." className="rounded-none mt-1" />
            </div>

            {/* Entreprise */}
            <div className="pt-2 border-t border-[#E0E0DB]">
              <p className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider mb-3">Entreprise attributaire</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Nom entreprise</Label>
                  <EntrepriseAutocomplete
                    value={form.entreprise_nom}
                    onChange={(val) => setForm({ ...form, entreprise_nom: val, entreprise_id: '' })}
                    onSelect={(ent) => setForm({
                      ...form,
                      entreprise_id: ent.id || ent._id || '',
                      entreprise_nom: ent.nom || ent.name,
                      entreprise_email: ent.email || form.entreprise_email,
                      entreprise_tel: ent.telephone || form.entreprise_tel,
                      entreprise_contact: ent.contact_principal || form.entreprise_contact,
                      entreprise_siret: ent.siret || form.entreprise_siret,
                    })}
                  />
                </div>
                <div>
                  <Label>Contact</Label>
                  <Input value={form.entreprise_contact} onChange={e => setForm({ ...form, entreprise_contact: e.target.value })} placeholder="M. Dupont" className="rounded-none mt-1" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.entreprise_email} onChange={e => setForm({ ...form, entreprise_email: e.target.value })} placeholder="contact@batipro.fr" className="rounded-none mt-1" />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input value={form.entreprise_tel} onChange={e => setForm({ ...form, entreprise_tel: e.target.value })} placeholder="01 23 45 67 89" className="rounded-none mt-1" />
                </div>
              </div>
            </div>

            {/* Financier */}
            <div className="pt-2 border-t border-[#E0E0DB]">
              <p className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider mb-3">Données financières</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Montant marché HT (€)</Label>
                  <Input type="number" step="0.01" value={form.montant_marche} onChange={e => setForm({ ...form, montant_marche: e.target.value })} placeholder="85 000" className="rounded-none mt-1" data-testid="lot-montant-input" />
                </div>
                <div>
                  <Label>Montant avenant HT (€)</Label>
                  <Input type="number" step="0.01" value={form.montant_avenant} onChange={e => setForm({ ...form, montant_avenant: e.target.value })} placeholder="0" className="rounded-none mt-1" />
                </div>
              </div>
            </div>

            {/* Statut & dates */}
            <div className="pt-2 border-t border-[#E0E0DB]">
              <p className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider mb-3">Statut & planification</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Statut</Label>
                  <Select value={form.statut} onValueChange={v => setForm({ ...form, statut: v })}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="lot-statut-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUT_MAP).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date OS</Label>
                  <Input type="date" value={form.ordre_service_date} onChange={e => setForm({ ...form, ordre_service_date: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <Label>Réf. OS</Label>
                  <Input value={form.ordre_service_ref} onChange={e => setForm({ ...form, ordre_service_ref: e.target.value })} placeholder="OS-001" className="rounded-none mt-1" />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">Annuler</Button>
              <Button type="submit" disabled={saving} className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none" data-testid="lot-submit-button">
                {saving ? 'Enregistrement...' : editing ? 'Enregistrer' : 'Ajouter le lot'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function EntrepriseAutocomplete({ value, onChange, onSelect }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const search = (q) => {
    onChange(q);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        // Utilise /entreprises/search qui combine entreprises_annuaire + users role=entreprise
        const { data } = await axios.get(`${API_URL}/api/entreprises/search?q=${encodeURIComponent(q)}`, { withCredentials: true });
        // Normaliser le champ nom (search renvoie soit .nom soit .name)
        const norm = (data || []).map(e => ({ ...e, nom: e.nom || e.name }));
        setSuggestions(norm);
        setShowSuggestions(norm.length > 0);
      } catch { setSuggestions([]); }
      setLoading(false);
    }, 300);
  };

  return (
    <div ref={wrapperRef} className="relative mt-1">
      <Input
        value={value}
        onChange={e => search(e.target.value)}
        onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
        placeholder="Chercher ou saisir une entreprise..."
        className="rounded-none"
        data-testid="lot-entreprise-nom"
      />
      {showSuggestions && (
        <div className="absolute z-50 top-full left-0 right-0 bg-white border border-[#E0E0DB] shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map(ent => (
            <button
              key={ent.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-[#F5F5F3] transition-colors text-sm flex items-center gap-2"
              onClick={() => { onSelect(ent); setShowSuggestions(false); }}
              data-testid={`autocomplete-ent-${ent.id}`}
            >
              <Buildings size={14} className="text-[#C75B39] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="font-medium text-[#2C2C2C]">{ent.nom}</span>
                {ent.activite && <span className="text-[#6B6B6B] ml-2">— {ent.activite}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Sprint D #23 — Modale documents marché
const MARCHE_DOC_TYPES = [
  { value: 'marche_signe', label: 'Marché signé' },
  { value: 'ordre_service', label: 'OS de démarrage' },
  { value: 'planning', label: 'Planning entreprise' },
  { value: 'caution', label: 'Caution bancaire' },
  { value: 'assurance', label: "Attestation d'assurance" },
  { value: 'autre', label: 'Autre' },
];

function MarcheDocumentsDialog({ chantierId, lot, onClose, onChanged, canWrite = true }) {
  const [items, setItems] = useState(lot?.documents || []);
  const [pendingType, setPendingType] = useState('marche_signe');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/marches/${lot.id}/documents`, { withCredentials: true });
      setItems(data?.items || []);
      onChanged?.();
    } catch (err) { /* noop */ }
  }, [chantierId, lot, onChanged]);

  useEffect(() => { reload(); }, [reload]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Fichier trop lourd (max 8 MB)'); return; }
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/marches/${lot.id}/documents`,
        { filename: file.name, content_base64: b64, type: pendingType },
        { withCredentials: true }
      );
      toast.success('Document ajouté');
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeDoc = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`${API_URL}/api/chantiers/${chantierId}/marches/${lot.id}/documents/${docId}`, { withCredentials: true });
      toast.success('Document supprimé');
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const fmtSize = (b) => !b ? '' : b < 1024 ? `${b} o` : b < 1048576 ? `${(b / 1024).toFixed(1)} Ko` : `${(b / 1048576).toFixed(1)} Mo`;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl p-0" data-testid="marche-documents-modal">
        <DialogHeader className="px-4 py-3 border-b border-[#E0E0DB] bg-[#FAFAF8]">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
            <Paperclip size={18} className="text-[#1E3A5F]" weight="duotone" />
            Documents marché — {lot.code} {lot.nom}
          </DialogTitle>
        </DialogHeader>
        <div className="p-4">
          {canWrite && (
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <select
              value={pendingType}
              onChange={(e) => setPendingType(e.target.value)}
              className="border border-[#E0E0DB] px-2 py-1 text-xs rounded-none bg-white"
              data-testid="marche-doc-type-select"
            >
              {MARCHE_DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input ref={fileInputRef} type="file" onChange={handleFile} className="hidden" disabled={uploading} />
            <Button variant="outline" size="sm" className="rounded-none gap-1 text-xs" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="marche-doc-upload">
              {uploading ? <Spinner size={14} className="animate-spin" /> : <Upload size={14} weight="bold" />}
              {uploading ? 'Envoi…' : 'Ajouter un document'}
            </Button>
            <span className="text-[10px] text-[#9A9A9A]">Max 8 Mo</span>
          </div>
          )}

          {items.length === 0 ? (
            <p className="text-xs text-[#9A9A9A] italic" data-testid="marche-doc-empty">Aucun document associé à ce marché</p>
          ) : (
            <ul className="divide-y divide-[#F0F0EE] border border-[#E0E0DB]" data-testid="marche-doc-list">
              {items.map((d) => {
                const typeLabel = MARCHE_DOC_TYPES.find((t) => t.value === d.type)?.label || d.type;
                return (
                  <li key={d.id} className="px-3 py-2 flex items-center gap-2" data-testid={`marche-doc-item-${d.id}`}>
                    <FileText size={14} className="text-[#9A9A9A] flex-shrink-0" />
                    <a href={`${API_URL}${d.url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1E3A5F] hover:underline flex-1 truncate">
                      {d.filename}
                    </a>
                    <span className="text-[10px] uppercase tracking-wide text-[#9A9A9A] bg-[#F5F5F3] px-2 py-0.5">
                      {typeLabel}
                    </span>
                    <span className="text-[10px] text-[#9A9A9A] whitespace-nowrap">{fmtSize(d.size)}</span>
                    {canWrite && (
                    <button onClick={() => removeDoc(d.id)} className="text-[#9A9A9A] hover:text-[#8B3A3A]" data-testid={`marche-doc-delete-${d.id}`}>
                      <Trash size={12} />
                    </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
